import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, TrendingUp, AlertTriangle, Shield, Clock,
  Car, HardHat, Activity, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const peakHours = [
    { time: '8:00 - 10:00 AM', level: 'High', color: 'bg-red-500' },
    { time: '12:00 - 1:30 PM', level: 'Medium', color: 'bg-yellow-500' },
    { time: '5:00 - 8:00 PM', level: 'High', color: 'bg-red-500' },
    { time: '10:00 PM - 6:00 AM', level: 'Low', color: 'bg-green-500' },
  ];

  const dailyStats = [
    { label: 'Challans Issued Today', value: '142', icon: Car, trend: '+12%', trendUp: true },
    { label: 'Accidents Reported', value: stats?.total_reports || '0', icon: AlertTriangle, trend: 'This month', trendUp: false },
    { label: 'Helmet Compliance', value: '67%', icon: HardHat, trend: '+5%', trendUp: true },
    { label: 'Violations Today', value: stats?.violations || '0', icon: Shield, trend: 'Active', trendUp: false },
  ];

  const recentActivity = [
    { text: 'Signal jumping reported near Jagat Circle', time: '12 min ago', type: 'violation' },
    { text: 'Pothole fixed on Station Road', time: '45 min ago', type: 'resolved' },
    { text: 'Wrong parking cleared near SuperMarket', time: '1 hr ago', type: 'resolved' },
    { text: 'Broken signal reported at Humnabad Road', time: '2 hrs ago', type: 'blackspot' },
    { text: 'Triple riding caught near University Gate', time: '3 hrs ago', type: 'violation' },
    { text: 'Speed breaker installed on Ring Road', time: '5 hrs ago', type: 'resolved' },
  ];

  const topAreas = [
    { name: 'Jagat Circle', reports: 45, severity: 'High' },
    { name: 'Super Market Area', reports: 38, severity: 'High' },
    { name: 'University Road', reports: 32, severity: 'Medium' },
    { name: 'Ring Road', reports: 28, severity: 'Medium' },
    { name: 'Humnabad Cross', reports: 22, severity: 'Medium' },
    { name: 'Station Road', reports: 18, severity: 'Low' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="dashboard-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Traffic Dashboard</h1>
          <p className="text-sm text-muted-foreground">Real-time traffic data for Kalaburagi district</p>
        </div>
        <Badge variant="outline" className="ml-auto gap-1 text-xs">
          <Activity className="w-3 h-3 text-green-500 animate-pulse" /> Live
        </Badge>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="dashboard-stats">
        {dailyStats.map((s, i) => (
          <Card key={s.label} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start justify-between">
                <s.icon className="w-5 h-5 text-primary" />
                <span className={`text-xs font-medium ${s.trendUp ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {s.trend}
                </span>
              </div>
              <p className="text-3xl font-bold mt-3">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Peak Traffic Hours */}
        <Card data-testid="peak-hours-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Peak Traffic Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {peakHours.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-sm font-medium">{p.time}</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
                  <span className="text-xs font-medium">{p.level}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card data-testid="recent-activity-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentActivity.map((a, i) => (
              <div key={i} className="flex items-start gap-3 pb-3 border-b last:border-0 last:pb-0">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                  a.type === 'violation' ? 'bg-red-500' : a.type === 'resolved' ? 'bg-green-500' : 'bg-orange-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{a.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.time}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Top Reported Areas */}
        <Card data-testid="top-areas-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Top Reported Areas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {topAreas.map((area, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-muted-foreground w-5">{i + 1}</span>
                  <span className="text-sm font-medium">{area.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        area.severity === 'High' ? 'bg-red-500' : area.severity === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(area.reports / 45) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground w-8">{area.reports}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Violation Category Breakdown */}
      {stats?.violation_categories?.length > 0 && (
        <Card className="mt-6" data-testid="violation-breakdown">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Violation Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {stats.violation_categories.map((cat, i) => (
                <div key={i} className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">{cat.count}</p>
                  <p className="text-xs text-muted-foreground mt-1">{cat.category}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
